/**
 * API Service - Capa de abstracción para todas las llamadas HTTP
 * Este servicio oculta los detalles de implementación de la API
 * y proporciona una interfaz limpia para los componentes
 * 
 * Ahora incluye autenticación JWT con Azure AD
 */

import configManager from '../utils/config';

class ApiService {
    constructor() {
        // La URL base se obtiene de forma ofuscada
        // Nunca se expone directamente en los componentes
        this.baseURL = null; // Se carga bajo demanda
        this.msalInstance = null; // Instancia de MSAL para obtener tokens
        this.account = null; // Cuenta activa del usuario
    }

    /**
     * Inicializa el servicio con la instancia de MSAL
     * Debe llamarse desde el componente App después de la autenticación
     * @param {Object} msalInstance - Instancia de @azure/msal-browser
     */
    setMsalInstance(msalInstance) {
        this.msalInstance = msalInstance;
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            this.account = accounts[0];
        }
    }

    /**
     * Obtiene la URL base (lazy loading)
     * @private
     */
    _getBaseURL() {
        if (!this.baseURL) {
            this.baseURL = configManager.getApiUrl();
        }
        return this.baseURL;
    }

    /**
     * Obtiene el token de acceso de Azure AD
     * @private
     */
    async _getAccessToken() {
        if (!this.msalInstance || !this.account) {
            const error = new Error('MSAL no está inicializado. Usuario no autenticado.');
            error.needsLogin = true;
            throw error;
        }

        try {
            // Intentar obtener token del cache primero (silent)
            const tokenRequest = {
                scopes: ['User.Read'], // Scopes mínimos necesarios
                account: this.account
            };

            const response = await this.msalInstance.acquireTokenSilent(tokenRequest);
            return response.accessToken;
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('Token expirado o no disponible en cache');
            }

            // Si el token expiró, necesita re-login
            if (error.errorCode === 'consent_required' || 
                error.errorCode === 'interaction_required' ||
                error.errorCode === 'login_required') {
                
                const loginError = new Error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
                loginError.needsLogin = true;
                throw loginError;
            }

            // Intentar renovar interactivamente solo si es otro error
            try {
                const response = await this.msalInstance.acquireTokenPopup({
                    scopes: ['User.Read']
                });
                return response.accessToken;
            } catch (interactiveError) {
                const loginError = new Error('No se pudo renovar la sesión. Por favor, inicie sesión nuevamente.');
                loginError.needsLogin = true;
                throw loginError;
            }
        }
    }

    /**
     * Método privado para realizar peticiones HTTP
     * Incluye automáticamente el token JWT en el header Authorization
     * @private
     */
    async _request(endpoint, options = {}) {
        try {
            // Obtener el token de acceso
            const accessToken = await this._getAccessToken();

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${accessToken}`, // JWT Token de Azure AD
                    ...options.headers
                },
                ...options
            };

            const url = `${this._getBaseURL()}${endpoint}`;
            const response = await fetch(url, config);
            
            // Verificar respuesta
            if (!response.ok) {
                // Manejar errores de autenticación
                if (response.status === 401) {
                    const error = new Error('UNAUTHORIZED');
                    error.needsLogin = true;
                    throw error;
                }
                if (response.status === 403) {
                    throw new Error('FORBIDDEN');
                }
                if (response.status === 404) {
                    const notFoundError = new Error('NOT_FOUND');
                    notFoundError.notFound = true;
                    throw notFoundError;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            // Propagar needsLogin si existe
            if (error.needsLogin) {
                throw error;
            }

            // Propagar notFound si existe
            if (error.notFound) {
                throw error;
            }

            // Manejo centralizado de errores (sin exponer detalles)
            if (process.env.NODE_ENV !== 'production') {
                console.error(`Error en la petición a ${endpoint}:`, error);
            }
            throw this._handleError(error);
        }
    }

    /**
     * Manejo centralizado de errores
     * @private
     */
    _handleError(error) {
        // Errores de autenticación
        if (error.message === 'UNAUTHORIZED') {
            const authError = new Error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
            authError.needsLogin = true;
            return authError;
        }

        if (error.message === 'FORBIDDEN') {
            return new Error('No tiene permisos para realizar esta acción');
        }

        if (error.message.includes('MSAL no está inicializado')) {
            const authError = new Error('Debe iniciar sesión para continuar');
            authError.needsLogin = true;
            return authError;
        }

        // Errores de respuesta del servidor
        if (error.response) {
            switch (error.response.status) {
                case 404:
                    return new Error('Recurso no encontrado');
                case 401:
                    const err401 = new Error('Su sesión ha expirado');
                    err401.needsLogin = true;
                    return err401;
                case 403:
                    return new Error('Acceso denegado');
                case 500:
                    return new Error('Error interno del servidor');
                default:
                    return new Error('Error en la petición');
            }
        } else if (error.request) {
            // Error de red
            return new Error('Error de conexión. Verifique su red');
        } else {
            // Otro tipo de error
            return error;
        }
    }

    // ==================== MÉTODOS DE USUARIOS ====================

    /**
     * Buscar un usuario específico
     */
    async searchUser(usuario) {
        return this._request('srchUser', {
            method: 'POST',
            body: JSON.stringify({ usuario })
        });
    }

    /**
     * Obtener lista completa de usuarios
     */
    async getUserList() {
        return this._request('getuserlist', {
            method: 'GET'
        });
    }

    /**
     * Obtener usuarios de un admin específico
     */
    async getUsersByAdmin() {
        return this._request('getuserlistbyuser', {
            method: 'GET'
        });
    }

    /**
     * Agregar un nuevo usuario
     */
    async addUser(usuario, userData) {
        return this._request('addNewUser', {
            method: 'POST',
            body: JSON.stringify({ 
                usuario, 
                data: userData 
            })
        });
    }

    /**
     * Actualizar lista de usuarios
     */
    async updateUserList(arrayData) {
        return this._request('updateUserList', {
            method: 'POST',
            body: JSON.stringify({ arrayData })
        });
    }

    // ==================== MÉTODOS DE DATOS ====================

    /**
     * Buscar números
     */
    async searchNumber(searchData) {
        return this._request('srchNumero', {
            method: 'POST',
            body: JSON.stringify(searchData)
        });
    }

    /**
     * Cargar información a la base de datos
     */
    async uploadData(uploadData) {
        return this._request('uploadInfoBD', {
            method: 'POST',
            body: JSON.stringify(uploadData)
        });
    }
}

// Exportar una instancia única del servicio (Singleton)
const apiService = new ApiService();
export default apiService;
